import { useState, type FormEvent } from "react";
import { ShieldCheck } from "lucide-react";
import { passwordSchema, type AuthSessionView } from "@ubisafe/shared";
import { BrandLogo } from "../components/BrandLogo";
import { PasswordRequirements } from "../components/PasswordRequirements";
import { api, ApiClientError } from "../lib/api";

interface ChangePasswordPageProps {
  onChanged: (session: AuthSessionView) => void;
}

export function ChangePasswordPage({ onChanged }: ChangePasswordPageProps) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    const parsed = passwordSchema.safeParse(newPassword);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "A senha não atende aos requisitos.");
      return;
    }
    if (newPassword !== confirmation) {
      setError("A confirmação não corresponde à nova senha.");
      return;
    }
    if (currentPassword === newPassword) {
      setError("A nova senha deve ser diferente da senha temporária.");
      return;
    }

    setLoading(true);
    try {
      const session = await api.changePassword({ currentPassword, newPassword: parsed.data });
      onChanged(session);
    } catch (requestError) {
      setError(requestError instanceof ApiClientError ? requestError.message : "Não foi possível alterar a senha.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-layout auth-layout--compact">
      <section className="auth-hero">
        <BrandLogo />
        <div className="auth-hero__content">
          <span className="eyebrow">Primeiro acesso</span>
          <h1>Proteja sua conta antes de continuar.</h1>
          <p>A senha temporária funciona somente para este acesso inicial.</p>
        </div>
      </section>
      <section className="auth-panel">
        <div className="auth-card">
          <div className="auth-card__icon" aria-hidden="true"><ShieldCheck /></div>
          <span className="eyebrow eyebrow--dark">Troca obrigatória</span>
          <h2>Definir senha pessoal</h2>
          <form onSubmit={(event) => void submit(event)} noValidate>
            <label htmlFor="current-password">Senha temporária</label>
            <input id="current-password" type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} autoComplete="current-password" />

            <label htmlFor="new-password">Nova senha</label>
            <input id="new-password" type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} autoComplete="new-password" />
            <PasswordRequirements password={newPassword} />

            <label htmlFor="new-password-confirmation">Confirmar nova senha</label>
            <input id="new-password-confirmation" type="password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} autoComplete="new-password" />

            {error && <div className="form-alert form-alert--error" role="alert">{error}</div>}
            <button className="primary-button primary-button--full" type="submit" disabled={loading}>
              {loading ? "Salvando…" : "Alterar senha e continuar"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
